import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, ReactElement } from 'react';
import { FormSpy } from 'react-final-form';

import {
  Field,
  Form,
  IFormContentProps,
  IImageLoadHandlerParams,
  ImageType,
  ImageUploader,
  MultiLineField,
  RadioGroup,
  TextField,
} from '@gem/components';
import { useTranslation } from '@gem/modules/i18n';
import { Button, ButtonTypes, customScrollStyles, PopupFooter } from '@gem/ui-kit';

import { Avatar, AvatarTypes, TAvatarBackgroundColorIndex } from 'APP/components/Avatar/Avatar';
import { Features } from 'APP/packages/features/Features';

import {
  IGroupFormInitialValues,
  TFormSubmitHandler,
  TFormUpdateHandler,
} from '../GroupForm.types';
import {
  FORM_ID,
  GROUP_DESCRIPTION_MAX_LENGTH,
  GROUP_NAME_MAX_LENGTH,
} from './GroupFormGeneral.constants';
import { useGroupFormGeneralPresenter } from './GroupFormGeneral.presenter';
import styles from './GroupFormGeneral.styles.m.css';
import { GroupFormGeneralDeepLinkField } from './GroupFormGeneralDeepLinkField/GroupFormGeneralDeepLinkField';
import { GroupFormGeneralMembers } from './GroupFormGeneralMembers/GroupFormGeneralMembers';

interface IGroupFormGeneralProps {
  initialValues: IGroupFormInitialValues;
  membersIds: string[];
  avatarTitle?: string;
  avatarColor?: TAvatarBackgroundColorIndex;
  isEditMode?: boolean;
  onUpdateGroupData: TFormUpdateHandler;
  onSubmit: TFormSubmitHandler;
  generalBottomDescription?: string;
  onFormStateChange(state: Partial<IFormContentProps>): void;
  onImageLoad(params: IImageLoadHandlerParams): void;
}

export const GroupFormGeneral: FC<IGroupFormGeneralProps> = observer((props) => {
  const {
    initialValues,
    membersIds,
    avatarTitle,
    avatarColor,
    isEditMode = false,
    generalBottomDescription,
    onUpdateGroupData,
    onSubmit,
    onFormStateChange,
    onImageLoad,
  } = props;

  const presenter = useGroupFormGeneralPresenter({ initialValues, isEditMode });
  const { t } = useTranslation();

  return (
    <Form
      initialValues={presenter.initialValues}
      subscription={{
        submitting: true,
        pristine: presenter.pristine,
        invalid: true,
        validating: true,
      }}
      onSubmit={onSubmit}
    >
      {({
        handleSubmit,
        submitting,
        pristine,
        invalid,
        validating,
      }: IFormContentProps): ReactElement => (
        <>
          <FormSpy
            subscription={{ submitting: true, pristine: presenter.pristine }}
            onChange={onFormStateChange}
          />

          <FormSpy
            subscription={{ values: true, initialValues: false }}
            onChange={onUpdateGroupData}
          />

          <form
            id={FORM_ID}
            className={classNames(styles.form, customScrollStyles.container)}
            onSubmit={handleSubmit}
          >
            <div className={styles.fields}>
              <Field
                name="avatarUrl"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                component={ImageUploader}
                imageType={ImageType.ROUND}
                onLoad={onImageLoad}
                renderImage={(url: string): ReactElement => (
                  <Avatar
                    url={url}
                    type={AvatarTypes.Round_40}
                  />
                )}
                customNoImage={
                  avatarTitle &&
                  avatarColor && (
                    <Avatar
                      type={AvatarTypes.Round_40}
                      title={avatarTitle}
                      backgroundColorIndex={avatarColor}
                    />
                  )
                }
              />

              <Field
                isNeedTrim
                name="name"
                label={t('group_name_hint')}
                component={TextField}
                className={styles.gap}
                maxLength={GROUP_NAME_MAX_LENGTH}
                validate={presenter.nameValidate}
              />
              <Field
                isNeedTrim
                name="description"
                label={t('channel_description_hint')}
                component={MultiLineField}
                className={styles.gap}
                maxLength={GROUP_DESCRIPTION_MAX_LENGTH}
              />
              {Features.isOpenChatEnabled() && (
                <RadioGroup
                  className={styles.radioGap}
                  name="type"
                  title={t('group_creation_type')}
                  options={presenter.radioOptions}
                  //TODO: remove after moving RadioGroup to new forms
                  validateFields={['deepLinkAlias'] as never[]}
                />
              )}

              {Features.isOpenChatEnabled() && (
                <Field
                  isNeedTrim
                  name="deepLinkAlias"
                  component={GroupFormGeneralDeepLinkField}
                  validate={presenter.aliasValidate}
                />
              )}
            </div>

            <GroupFormGeneralMembers membersIds={membersIds} />
          </form>
          {generalBottomDescription && (
            <div className={styles.generalBottomDescription}>{generalBottomDescription}</div>
          )}
          <PopupFooter>
            <Button
              type={ButtonTypes.Submit}
              form={FORM_ID}
              isLoading={submitting}
              isDisabled={pristine || invalid || validating}
              theme="primary"
            >
              {isEditMode ? t('common_save') : t('create_new_group_chat_button')}
            </Button>
          </PopupFooter>
        </>
      )}
    </Form>
  );
});
